/**
 * Created by fers on 27/08/2017.
 */

import ExpressionBuilderParser from './ExpressionBuilderParser';
import {mergeArray} from '../util/VariableConversion';
import {newMatchJSON} from '../util/json-path/JSONPath';

let invokeConversionFunction = require('../util/VariableConversion').invokeConversionFunction;

export class Builder {
    onOperator(operator, children) {
        throw new Error("Not implemented");
    }

    onVariable(variable) {
        throw new Error("Not implemented");
    }

    onFunction(funcName, args) {
        throw new Error("Not implemented");
    }

    onValue(value) {
        throw new Error("Not implemented");
    }

    onBracket(child) {
        throw new Error("Not implemented");
    }

    onLogicalExpression(expr1, operator, expr2) {
        throw new Error("Not implemented");
    }

    onConditional(condition, expr1, expr2) {
        throw new Error("Not implemented");
    }

    onText(text) {
        throw new Error("Not implemented");
    }

    onLambda(variable, expr1) {
        throw new Error("Not implemented");
    }

    onExpression(expression) {
        throw new Error("Not implemented");
    }
}

class EvaluateBuilder extends Builder {
    constructor (getVariable) {
        super();

        this.getVariable = getVariable;
    }

    fixUndefined(value) {
        if (typeof value === 'undefined' || value === null) {
            return '';
        }

        return value;
    }

    onOperator(operator, children) {
        return () => {
            let result = null;
            let isFirst = true;

            for (let a of children) {
                if (isFirst) {
                    result = a();
                    isFirst = false;
                }  else if (operator === '+') {
                    if (result === undefined || result === null) {
                        result = this.fixUndefined(a());
                    } else {
                        result = result + this.fixUndefined(a());
                    }
                } else if (operator === '-') {
                    result = result - a();
                } else if (operator === '*') {
                    result = result * a();
                } else if (operator === '/') {
                    result = result / a();
                }
            }

            return result;
        };
    }

    removeArray(item) {
        if (item && Array.isArray(item) && item.length === 1) {
            return item[0];
        }
        return item;
    }

    onLogicalExpression(expr1, operator, expr2) {
        return () => {
            if (!operator && !expr2) {
                return expr1();
            } else if (operator === "===" || operator === "==") {
                let leftValue = this.removeArray(expr1());
                let rightValue = this.removeArray(expr2());
                if (typeof leftValue === 'boolean') {
                    leftValue = String(leftValue);
                }
                if (typeof rightValue === 'boolean') {
                    rightValue = String(rightValue);
                }
                // eslint-disable-next-line eqeqeq
                return leftValue == rightValue;
            } else if (operator === "!==" || operator === "!=") {
                let leftValue = this.removeArray(expr1());
                let rightValue = this.removeArray(expr2());
                if (typeof leftValue === 'boolean') {
                    leftValue = String(leftValue);
                }
                if (typeof rightValue === 'boolean') {
                    rightValue = String(rightValue);
                }
                // eslint-disable-next-line eqeqeq
                return leftValue != rightValue;
            } else if (operator === ">") {
                return this.removeArray(expr1()) > this.removeArray(expr2());
            } else if (operator === "~=") {
                return String(this.removeArray(expr1())).indexOf(this.removeArray(expr2())) !== -1;
            } else if (operator === "<") {
                return this.removeArray(expr1()) < this.removeArray(expr2());
            } else if (operator === ">=") {
                return this.removeArray(expr1()) >= this.removeArray(expr2());
            } else if (operator === "<=") {
                return this.removeArray(expr1()) <= this.removeArray(expr2());
            } else {
                throw new Error("Invalid operator " + operator);
            }
        };
    }

    onLambda(variable, expr1) {
        return () => {
            return (row) => {
                /* override getVariable to support new variable stored in 'variable' */
                let origGetVariable = this.getVariable;
                this.getVariable = (newVariable) => {
                    if (newVariable === variable) {
                        return row;
                    }

                    if (newVariable.startsWith(variable + ".")) {
                        let expression = '$.' + newVariable.substring(variable.length + 1);

                        let result = newMatchJSON(expression, row);
                        /* JSONPath returns array */
                        if (Array.isArray(result) && result.length === 1) {
                            return result[0];
                        }
                        return result;
                    }

                    return origGetVariable(newVariable);
                };

                let result = expr1();

                this.getVariable = origGetVariable;

                return result;
            };
        };
    }

    onConditional(condition, expr1, expr2) {
        return () => {
            if (condition()) {
                return expr1();
            } else {
                return expr2();
            }
        };
    }

    onVariable(variable) {
        return () => this.getVariable(variable);
    }

    onFunction(funcName, args) {
        return () => invokeConversionFunction(funcName, args.map((a) => a && typeof a === "function" ? a() : a));
    }

    onValue(value) {
        return () => value;
    }

    onBracket(child) {
        return () => child();
    }

    onText(text) {
        return () => text;
    }

    onExpression(expression) {
        return () => {
            return expression();
        };
    }
}

export class UsedVariablesBuilder extends Builder {
    constructor () {
        super();

        this.variables = {};
    }

    onOperator(operator, children) {
        return null;
    }

    onVariable(variable) {
        this.variables[variable] = true;

        return null;
    }

    onFunction(funcName, args) {
        return null;
    }

    onValue(value) {
        return value;
    }

    onBracket(child) {
        return child;
    }

    onText(text) {
        return text;
    }

    onExpression(expression) {
        return expression;
    }

    onLogicalExpression(expr1, operator, expr2) {
        return null;
    }

    onConditional(condition, expr1, expr2) {
        return null;
    }

    onLambda(variable, expr1) {
        return null;
    }

}

export function testEvaluateExpression(expression : string, variables) {
    return joinedEvaluateExpression(expression, (variable) => variables[variable]);
}

export function joinedEvaluateExpression(expression : string, getVariable : Function) {
    if (expression === null || expression === undefined) {
        return null;
    }

    if (expression && expression.indexOf('{') === -1) {
        return [expression];
    }

    let result = evaluateExpression(expression, getVariable);
    if (result === null) {
        return null;
    }

    return result.filter((a) => a !== null ? true : false).join("");
}

export function evaluateExpression(expression : string, getVariable : Function) {
    if (expression === null || expression === undefined) {
        return null;
    }

    return expressionBuilder(expression, new EvaluateBuilder(getVariable));
}

export function expressionBuilder(expression : string, builder : Builder) {
    if (expression === null || expression === undefined) {
        return null;
    }

    let result = ExpressionBuilderParser.parse(expression, {
        builder: builder,
    });

    /* convert function to result */
    result = result.map((a) => a && typeof a === "function" ? a() : a);

    return mergeArray(result);
}
